import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { AccountRegistrationForm, AccountFormData } from './AccountRegistrationForm';
import { CompanyRegistrationForm, CompanyFormData } from './CompanyRegistrationForm';
import { supabase } from '@/integrations/supabase/client';
import { anonSupabase } from '@/integrations/supabase/anonClient';
import { toast } from 'sonner';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { getErrorDetails } from '@/utils/registrationErrorHandling';

interface RegistrationModalProps {
  onClose: () => void;
  onSwitchToSignIn: () => void;
}

export function RegistrationModal({ onClose, onSwitchToSignIn }: RegistrationModalProps) {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [accountData, setAccountData] = useState<AccountFormData | null>(null);
  const [validationProgress, setValidationProgress] = useState<string>('');
  
  const handleAccountSubmit = (data: AccountFormData) => {
    setAccountData(data);
    setStep(2);
  };
  
  const handleBackToAccount = () => {
    setStep(1);
  };
  
  const handleCompanySubmit = async (companyData: CompanyFormData) => {
    if (!accountData) {
      toast.error("Account data missing, please try again");
      setStep(1);
      return;
    }
    
    setIsLoading(true);
    setValidationProgress('Validating registration details...');

    // Approval-first path for non-FCA firms: NO auth user is created until an
    // admin approves the request. This makes it impossible for a half-built
    // signup to leave a profile in limbo.
    if (companyData.firmType === 'outsourced_paraplanner' || companyData.firmType === 'other') {
      try {
        setValidationProgress('Submitting your request for review...');
        const { data, error } = await anonSupabase.functions.invoke(
          'submit-manual-approval-request',
          {
            body: {
              email: accountData.email,
              password: accountData.password,
              full_name: accountData.fullName,
              company_name: companyData.companyName,
              firm_type: companyData.firmType,
              fca_number: companyData.fcaNumber || null,
              contact_number: companyData.contactNumber || null,
              contact_address: companyData.contactAddress || null,
              office_name: companyData.officeName || null,
              job_role: companyData.jobRole,
              justification: companyData.justification || null,
            },
          },
        );
        if (error) throw new Error((error as any).message || 'Failed to submit request');
        if ((data as any)?.error) throw new Error((data as any).error);

        toast.success(
          "Request submitted. We'll email you within 2 business days once an admin reviews it.",
        );
        window.location.href = '/registration-pending?type=manual-approval';
        return;
      } catch (err: any) {
        console.error('Manual approval submission failed:', err);
        toast.error(err?.message || 'Failed to submit your request. Please try again.');
        setIsLoading(false);
        setValidationProgress('');
        return;
      }
    }

    // FCA-regulated path (auth user is created, so we wrap with rollback on failure).
    let createdUserId: string | null = null;
    try {
      // STEP 1: Validate FCA number if provided (BEFORE creating auth user)
      let fcaValidationResult = null;
      let trialEligibilityData = null;
      
      if (companyData.firmType === 'fca_regulated' && companyData.fcaNumber) {
        setValidationProgress('Validating FCA number...');
        
        const { data: fcaData, error: fcaError } = await anonSupabase.functions.invoke('validate-fca', {
          body: { fcaNumber: companyData.fcaNumber }
        });

        if (fcaError) {
          throw new Error('Failed to validate FCA number. Please try again.');
        }

        if (!fcaData?.isValid) {
          throw new Error('Invalid FCA number. Please check and try again.');
        }
        
        fcaValidationResult = fcaData;
        
        // STEP 2: Check trial eligibility (BEFORE creating auth user)
        setValidationProgress('Checking trial eligibility...');
        
        const { data: trialData, error: trialError } = await anonSupabase.functions.invoke('check-trial-eligibility', {
          body: { fcaNumber: companyData.fcaNumber }
        });

        if (trialError) {
          throw new Error('Failed to check trial eligibility. Please try again.');
        }

        // Check if this is a company access request
        if (trialData?.requiresCompanyApproval && trialData?.existingCompanyId) {
          // For company access requests, we DO create the auth user but with special status
          setValidationProgress('Creating user account for company access request...');
          
          const { data: authData, error: authError } = await supabase.auth.signUp({
            email: accountData.email,
            password: accountData.password,
            options: {
              emailRedirectTo: `${window.location.origin}/auth/callback`,
              data: {
                full_name: accountData.fullName,
                // Intentionally omit company_name / fca_number so handle_new_user
                // does NOT try to create or link a company. We link via RPC below.
                job_role: companyData.jobRole,
                contact_number: companyData.contactNumber || '',
                contact_address: companyData.contactAddress || '',
                firm_type: companyData.firmType,
                approval_status: 'pending_approval'
              }
            }
          });

          if (authError) throw authError;
          if (!authData.user) throw new Error("Failed to create user account");

          // Link profile to existing company + create approval request via RPC
          const { error: rpcError } = await supabase.rpc('request_company_access', {
            p_company_id: trialData.existingCompanyId,
            p_job_role: companyData.jobRole,
            p_office_location_name: companyData.officeName || null,
            p_contact_number: companyData.contactNumber || null,
            p_contact_address: companyData.contactAddress || null,
          });

          if (rpcError) {
            console.error('[Registration] request_company_access failed:', rpcError);
            throw new Error(rpcError.message || 'Failed to create access request');
          }

          // Notify company admins (in-app notification + future email)
          try {
            const { error: notifyError } = await supabase.functions.invoke('notify-company-admin', {
              body: {
                userId: authData.user.id,
                companyId: trialData.existingCompanyId,
                userName: accountData.fullName,
                userEmail: accountData.email,
                jobRole: companyData.jobRole,
              }
            });
            if (notifyError) {
              console.error('[Registration] notify-company-admin error:', notifyError);
            }
          } catch (notifyErr) {
            console.error('[Registration] notify-company-admin invocation failed:', notifyErr);
          }

          toast.success("Access request submitted! The company administrator will review your request.");
          window.location.href = '/registration-pending?type=company-approval';
          return;
        }

        if (!trialData?.eligible) {
          throw new Error(trialData?.reason || 'This FCA number has already used a trial');
        }
        
        trialEligibilityData = trialData;
      }

      // STEP 3: Create auth user IMMEDIATELY with Supabase (secure password handling)
      setValidationProgress('Creating secure account...');
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: accountData.email,
        password: accountData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            full_name: accountData.fullName,
            company_name: companyData.companyName,
            fca_number: companyData.fcaNumber || null,
            office_location: companyData.officeName || '',
            office_location_id: companyData.officeLocationId || null,
            job_role: companyData.jobRole,
            contact_number: companyData.contactNumber || '',
            contact_address: companyData.contactAddress || '',
            firm_type: companyData.firmType,
            fca_firm_name: fcaValidationResult?.firmName || null,
            // All users start inactive until payment is processed
            approval_status: 'pending_payment',
            is_active: false
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Failed to create user account");

      const userId = authData.user.id;
      createdUserId = userId;

      // STEP 4: Generate registration token for payment tracking
      setValidationProgress('Preparing payment setup...');
      const registrationToken = crypto.randomUUID();

      // STEP 5: Create pending registration record
      setValidationProgress('Finalizing registration...');
      const { error: pendingError } = await supabase
        .from('pending_registrations')
        .insert({
          registration_token: registrationToken,
          user_id: userId,
          email: accountData.email,
          full_name: accountData.fullName,
          company_name: companyData.companyName,
          fca_number: companyData.fcaNumber || null,
          contact_number: companyData.contactNumber || '',
          contact_address: companyData.contactAddress || '',
          office_name: companyData.officeName || '',
          job_role: companyData.jobRole,
          fca_firm_name: fcaValidationResult?.firmName || null,
          firm_type: companyData.firmType,
          requires_manual_approval: false,
          approval_stage: 'awaiting_payment'
        });

      if (pendingError) throw pendingError;

      // STEP 6: Stripe checkout
      setValidationProgress('Preparing payment setup...');
      const { data: checkoutData, error: checkoutError } = await anonSupabase.functions.invoke(
        'create-registration-checkout',
        {
          body: {
            registrationToken,
            email: accountData.email,
            fullName: accountData.fullName
          }
        }
      );

      if (checkoutError) {
        const errorDetails = getErrorDetails(checkoutError, 'payment');
        throw new Error(errorDetails.userMessage);
      }

      if (checkoutData?.url) {
        // Successful handoff — DO NOT roll back if user navigates away from Stripe;
        // the cleanup edge function will sweep abandoned ones later.
        createdUserId = null;
        window.location.href = checkoutData.url;
        toast.success("Redirecting to payment setup...");
      } else {
        throw new Error('Failed to create payment session. Please try again or contact support.');
      }
    } catch (error: any) {
      console.error("Registration error:", error);

      // Roll back the partially-created auth user / profile / company so the
      // user isn't left in limbo and can retry their signup.
      if (createdUserId) {
        try {
          await anonSupabase.functions.invoke('rollback-failed-registration', {
            body: { user_id: createdUserId },
          });
        } catch (rbErr) {
          console.error('Rollback failed:', rbErr);
        }
      }

      let errorMessage = error.message || "An error occurred during registration. Please try again.";
      if (error.message && (
        error.message.includes('payment') ||
        error.message.includes('checkout') ||
        error.message.includes('stripe')
      )) {
        const errorDetails = getErrorDetails(error, 'payment');
        errorMessage = errorDetails.userMessage;
        if (errorDetails.action === 'retry') {
          errorMessage += ' You can try again with different payment details.';
        } else if (errorDetails.action === 'contact_support') {
          errorMessage += ' Please contact support@comparare.co.uk for assistance.';
        }
      }
      if (error.message && error.message.includes('FCA')) {
        const errorDetails = getErrorDetails(error, 'fca_validation');
        errorMessage = errorDetails.userMessage;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setValidationProgress('');
    }
  };

  return (
    <Card className="w-full max-w-md relative animate-fade-in max-h-[90vh] flex flex-col">
      <Button 
        variant="ghost" 
        size="icon" 
        className="absolute right-2 top-2 z-10" 
        onClick={onClose}
        disabled={isLoading}
      >
        <X className="h-4 w-4" />
      </Button>
      
      <CardHeader className="flex-shrink-0">
        <CardTitle>
          {step === 1 ? "Create Account" : "Company Details"}
        </CardTitle>
        <CardDescription>
          {step === 1 
            ? "Register for a new Comparare account" 
            : "Tell us about your company"}
        </CardDescription>
        {validationProgress && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{validationProgress}</span>
          </div>
        )}
      </CardHeader>
      
      <ScrollArea className="flex-1 min-h-0">
        <div className="max-h-[calc(90vh-12rem)] overflow-y-auto px-6 pb-6">
          {step === 1 ? (
            <AccountRegistrationForm 
              onSubmit={handleAccountSubmit}
              onCancel={onClose}
              onSignInClick={onSwitchToSignIn}
            />
          ) : (
            <CompanyRegistrationForm 
              onSubmit={handleCompanySubmit}
              onBack={handleBackToAccount}
              isLoading={isLoading}
            />
          )}
        </div>
      </ScrollArea>
    </Card>
  );
}